import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Button,
  useMediaQuery,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HardwareRoundedIcon from "@mui/icons-material/HardwareRounded";

import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as ExcelExportIcon } from "../assets/img/icons/excel_export.svg";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { SecureImage } from "./SecureImage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const OrderDetailsTable = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const mfrId = state.mfrId;
  const customerDeliveryDate = state?.deliveryDate;
  const customerName = state?.customerName;
  const customerPhone = state?.customerPhone;
  const linkId = state?.linkId;
  const order_no = state?.order_no;
  const orderDeliveryDate = state?.deliveryDate;

  const token = localStorage.getItem("authToken");
  const currentLoggedUser = JSON.parse(localStorage.getItem("user"));
  const currentLoggedUserId = currentLoggedUser?.id;

  const [dialogIndex, setDialogIndex] = useState(-1);
  const [currentItems, setCurrentItems] = useState([]);
  const [ordersData, setOrdersData] = useState([]); // All records from API
  const [loading, setLoading] = useState(true);
  const [orderFlag, setOrderFlag] = useState("i");
  const [groupNotes, setGroupNotes] = useState({});
  const [rejectionPopupOpen, setRejectionPopupOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [itemsToReject, setItemsToReject] = useState(null);
  const [status, setStatus] = useState("All");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const isMobile = useMediaQuery("(max-width:600px)");

  // Fetch orders using merged API
  const fetchOrders = async () => {
    try {
      setLoading(true);

      // Build query params based on what filters are active
      const params = new URLSearchParams();

      if (order_no) params.append("orderNo", order_no);
      if (status !== "All") {
        // Map frontend status to backend status
        const statusMap = {
          New: "New",
          Confirmed: "Confirm(DM)",
          Accepted: "Accept(WH)",
          Rejected: "Reject(DM)",
          Unavailable: "Unavailable(WH)",
          Placed: "Order(DM)",
          Dispatched: "Dispatched(SP)",
        };
        params.append("status", statusMap[status] || status);
      }

      const url = `${API_BASE_URL}/api/fetchorder/getOrders?${params.toString()}`;
      const { data } = await axios.get(url);

      // Handle different response structures
      if (data.statusCode === 200) {
        const records = data.data?.records || [];
        setOrdersData(records);

        // Set order flag from first record
        if (records.length > 0) {
          setOrderFlag(records[0].flag || "i");
        }
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      openSnackbar("Failed to fetch orders", "error");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, [status]);

  // Process orders data to create stockData format
  const processedStockData = useMemo(() => {
    if (!ordersData || ordersData.length === 0) return [];

    // Flatten all selectedTagElements from all records
    const allItems = [];

    ordersData.forEach((order) => {
      if (
        order.selectedTagElements &&
        Array.isArray(order.selectedTagElements)
      ) {
        order.selectedTagElements.forEach((item) => {
          allItems.push({
            ...item,
            // Add order-specific metadata
            orderStatus: order.status,
            orderType: order.orderType,
            rejectedBy: order.rejectedBy,
            rejectedAt: order.rejectedAt,
            rejectedReason: order.rejectedReason,
          });
        });
      }
    });

    return allItems;
  }, [ordersData]);

  // Grouping logic (keeps your original algorithm)
  const groupItemsIntoSets = (items) => {
    if (!items || items.length === 0) return [];

    if (orderFlag === "i") {
      return items.map((item) => ({
        type: "individual",
        parent: item,
        children: [],
      }));
    }

    const sets = [];
    const processedTags = new Set();

    items.forEach((item) => {
      if (processedTags.has(item.tagno)) return;

      const children = items.filter(
        (child) => child.crosstag === item.tagno && child.tagno !== item.tagno
      );

      if (children.length > 0) {
        sets.push({
          type: "set",
          parent: item,
          children: children,
        });

        processedTags.add(item.tagno);
        children.forEach((ch) => processedTags.add(ch.tagno));
      } else if (!items.some((parent) => parent.tagno === item.crosstag)) {
        sets.push({
          type: "individual",
          parent: item,
          children: [],
        });
        processedTags.add(item.tagno);
      }
    });

    return sets;
  };

  const groupedData = useMemo(
    () => groupItemsIntoSets(processedStockData),
    [processedStockData, orderFlag]
  );

  // Calculate total gross for a group
  const calculateTotalGross = (group) => {
    const allItems = [group.parent, ...group.children];
    const total = allItems.reduce((sum, item) => {
      const gross = parseFloat(item.gross) || 0;
      return sum + gross;
    }, 0);
    return total.toFixed(3);
  };

  // Excel export
  const exportGroupedToExcel = () => {
    if (!groupedData || groupedData.length === 0) {
      openSnackbar("No data to export", "warning");
      return;
    }

    const rows = [];

    groupedData.forEach((group) => {
      const groupTag = group.parent?.tagno || "";
      const totalGross = group.type === "set" ? calculateTotalGross(group) : "";

      rows.push({
        GroupType: group.type === "set" ? "PARENT" : "INDIVIDUAL",
        GroupTag: groupTag,
        Tag: group.parent?.tagno || "",
        Item: group.parent?.itemtype_name || "",
        Design: group.parent?.design_name || "",
        Gross: group.parent?.gross || "",
        TotalGross: totalGross,
        Status: group.parent?.orderStatus || "",
        Note: groupNotes[group.parent?.tagno] || "",
      });

      if (group.children && group.children.length > 0) {
        group.children.forEach((child) => {
          rows.push({
            GroupType: "CHILD",
            GroupTag: groupTag,
            Tag: child.tagno || "",
            Item: child.itemtype_name || "",
            Design: child.design_name || "",
            Gross: child.gross || "",
            TotalGross: "",
            Status: child.orderStatus || "",
            Note: "",
          });
        });
      }
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "OrderDetails");

    const filename = `order-details-${new Date()
      .toLocaleDateString("en-IN")
      .replace(/\//g, "-")}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);

    openSnackbar("Excel exported successfully", "success");
  };

  // Place order
  const handlePlaceOrder = async (group) => {
    const note = groupNotes[group.parent.tagno] || "";

    const payload = {
      mfrId,
      linkId: state.linkId || "",
      customerDetails: {
        customerName: state.customerName || "",
        customerPhone: state.customerPhone || "",
        deliveryDate: state.deliveryDate || "",
        notes: note,
      },
      selectedStocks: [group.parent, ...group.children],
    };

    try {
      const url = `${API_BASE_URL}/api/orders/createOrderFromLink`;

      const { data } = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`, // make sure token exists
          "Content-Type": "application/json",
        },
      });

      // Optional: show a success notification to user
      console.log("Order created successfully:", data);
      openSnackbar("Order placed Succefully", "success");

      // Refresh data
      await fetchOrders();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Axios error handling
        if (error.response) {
          // Server responded with a status code
          console.error("Server Response Error:", error.response.data);
          if (error.response.status === 401) {
            // alert("Unauthorized! Please login again.");
            openSnackbar("Unauthorized! Please login again", "error");
            // Optionally redirect to login
          } else {
            // alert(
            //   `Error ${error.response.status}: ${
            //     error.response.data.message || "Something went wrong"
            //   }`
            // );
            openSnackbar(
              `Error ${error.response.status}: ${
                error.response.data.data.message || "Something went wrong"
              }`,
              "error"
            );
          }
        } else if (error.request) {
          // No response received
          console.error("No response from server:", error.request);
          // alert("No response from server. Please check your network.");
          openSnackbar(
            "No response from server. Please check your network.",
            "error"
          );
        } else {
          // Other errors
          console.error("Axios error:", error.message);
          // alert(`Error: ${error.message}`);
          openSnackbar(`Error: ${error.message}`, "error");
        }
      } else {
        // Non-Axios error
        console.error("Error:", error);
        // alert("An unexpected error occurred.");
        openSnackbar("An unexpected error occurred.", "error");
      }
    }
  };

  // Handle status updates
  const handleUpdateStatus = async (group, newStatus) => {
    const allItems = [group.parent, ...group.children];
    const stockItemTags = allItems.map((item) => item.tagno);

    const payload = {
      linkId,
      orderNo: order_no,
      selectedTags: stockItemTags,
      customerDetails: {
        customerName: customerName,
        customerPhone: customerPhone,
        deliveryDate: customerDeliveryDate,
      },
      mfrId,
      status: newStatus,
      rejectedBy: newStatus === "Reject(DM)" ? currentLoggedUserId : null,
      rejectedReason: newStatus === "Reject(DM)" ? rejectionReason : null,
    };

    try {
      const url = `${API_BASE_URL}/api/fetchorder/changeOrderStatus`;

      await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setRejectionPopupOpen(false);
      setRejectionReason("");

      if (newStatus === "Reject(DM)") {
        openSnackbar(
          `${stockItemTags.length} item${
            stockItemTags.length > 1 ? "s" : ""
          } rejected`,
          "success"
        );
      } else if (newStatus === "Confirm(DM)") {
        openSnackbar(
          `${stockItemTags.length} item${
            stockItemTags.length > 1 ? "s" : ""
          } confirmed`,
          "success"
        );
      }

      // Refresh data
      await fetchOrders();
    } catch (error) {
      console.error(error);
      openSnackbar("Failed to update status", "error");
    }
  };

  const openDialog = (group) => {
    setCurrentItems([group.parent, ...group.children]);
    setDialogIndex(0);
  };

  const closeDialog = () => {
    setDialogIndex(-1);
    setCurrentItems([]);
  };

  const navigateDialog = (direction) => {
    if (!currentItems.length) return;
    setDialogIndex((prev) => {
      if (direction === "prev")
        return prev === 0 ? currentItems.length - 1 : prev - 1;
      if (direction === "next")
        return prev === currentItems.length - 1 ? 0 : prev + 1;
      return prev;
    });
  };

  const currentItem = dialogIndex >= 0 ? currentItems[dialogIndex] : null;

  return (
    <Box sx={{ bgcolor: "#394A5C", minHeight: "100vh", p: isMobile ? 2 : 6 }}>
      {/* TOP ACTION BAR */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        {/* BACK BUTTON */}
        <Box
          sx={{
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIosNewRoundedIcon
            sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#42A996" }}
          />
        </Box>

        {/* HOME BUTTON */}
        <Box
          sx={{
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
          }}
          onClick={() => navigate("/admin-dash")}
        >
          <HomeRoundedIcon
            sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#42A996" }}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Excel Export */}
        <Box>
          <IconButton
            onClick={exportGroupedToExcel}
            sx={{
              bgcolor: "#FFF",
              borderRadius: 2,
              width: 36,
              height: 36,
              "&:hover": {
                bgcolor: "#1c4b42ff",
                "& svg": {
                  fill: "#ffffff", // <-- icon color on hover
                },
              },
              "& svg": {
                transition: "0.2s",
                fill: "#42A996", // <-- default icon color
              },
            }}
            aria-label="Export Excel"
          >
            <ExcelExportIcon
              style={{
                width: 18,
                height: 18,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* USER INFO & STATUS */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: { xs: 2, sm: 3 },
          mb: 3,
        }}
      >
        {/* ORDER & USER INFO */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {/* ORDER INFO */}
          <Box
            sx={{
              display: "flex",
              alignItems: { sm: "center", xs: "flex-start" },
              gap: 2,
              width: "100%",
              mb: 1,
            }}
          >
            {/* Order Number */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  bgcolor: "#ACD4",
                  p: 0.5,
                }}
              >
                <InventoryRoundedIcon
                  sx={{ fontSize: { xs: 24, sm: 28 }, fill: "#FFF" }}
                />
              </Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: 14, sm: 18 },
                  fontWeight: 600,
                }}
              >
                {order_no}
              </Typography>
            </Box>

            {/* Delivery Date */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  bgcolor: "#ACD4",
                  p: 0.5,
                }}
              >
                <CalendarMonthRoundedIcon
                  sx={{ fontSize: { xs: 24, sm: 28 }, fill: "#FFF" }}
                />
              </Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: 14, sm: 18 },
                  fontWeight: 600,
                }}
              >
                {orderDeliveryDate}
              </Typography>
            </Box>
          </Box>

          {/* USER INFO */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { sm: "row", xs: "column" },
              alignItems: { sm: "center", xs: "flex-start" },
              gap: { sm: 2, xs: 1 },
              width: "100%",
            }}
          >
            {/* Name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  bgcolor: "#ACD4",
                  p: 0.5,
                }}
              >
                <PersonRoundedIcon
                  sx={{ fontSize: { xs: 24, sm: 28 }, fill: "#FFF" }}
                />
              </Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: 14, sm: 18 },
                  fontWeight: 600,
                }}
              >
                {customerName}
              </Typography>
            </Box>

            {/* Phone */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="a"
                href={`tel:${customerPhone}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  bgcolor: "#ACD4",
                  p: 0.5,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "#4CAF50",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <LocalPhoneRoundedIcon
                  sx={{ fontSize: { xs: 24, sm: 28 }, fill: "#FFF" }}
                />
              </Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: 14, sm: 18 },
                  fontWeight: 600,
                }}
              >
                {customerPhone}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* STATUS DROPDOWN */}
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 160 },
            mt: { xs: 2, sm: 0 },
          }}
        >
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            displayEmpty
            sx={{ bgcolor: "#fff", borderRadius: 1 }}
          >
            {[
              "All",
              "New",
              "Confirmed",
              "Accepted",
              "Rejected",
              "Unavailable",
              "Placed",
              "Dispatched",
            ].map((statusOption) => (
              <MenuItem key={statusOption} value={statusOption}>
                {statusOption}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* TABLE */}
      <Box
        sx={{
          bgcolor: "#2A3A4B",
          borderRadius: 2,
          overflowX: { xs: "auto", md: "visible" },
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& table": {
            width: "max-content",
            minWidth: "100%",
            tableLayout: "auto !important",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Tag</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Item
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Design
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Gross
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Image
              </TableCell>
              {orderFlag === "s" && (
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Total Gross
                </TableCell>
              )}
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Note
              </TableCell>
              {status !== "Rejected" && status !== "Dispatched" && (
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Action
                </TableCell>
              )}
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Status
              </TableCell>
              {status === "Rejected" && (
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Reason
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={orderFlag === "s" ? 10 : 9}
                  sx={{ textAlign: "center", color: "#fff", py: 4 }}
                >
                  Loading...
                </TableCell>
              </TableRow>
            )}

            {!loading && groupedData.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={orderFlag === "s" ? 10 : 9}
                  sx={{ textAlign: "center", color: "#fff", py: 4 }}
                >
                  No orders found
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              groupedData.map((group, groupIndex) => {
                const isSet = group.type === "set";
                const allItems = [group.parent, ...group.children];

                // 👇 NEW ONLY-CHANGE: Determine background based on STATUS
                const rowStatus = group.parent.orderStatus;
                let bgColor =
                  rowStatus === "Reject(DM)"
                    ? "#fa00004c"
                    : rowStatus === "Accept(WH)" || rowStatus === "Confirm(DM)"
                    ? "#058d7030"
                    : rowStatus === "Unavailable(WH)"
                    ? "#ff730054"
                    : rowStatus === "Dispatched(SP)"
                    ? "#144e12ff"
                    : rowStatus === "Order(DM)"
                    ? "#5b6106ff"
                    : groupIndex % 2 === 0
                    ? "#1E2F40"
                    : "#2A3A4B";

                return (
                  <TableRow
                    key={groupIndex}
                    sx={{
                      bgcolor: bgColor,
                      "& td": {
                        paddingY: 1,
                        paddingX: 0,
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    {/* Tag column */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: index === 0 && isSet ? 600 : 400,
                              color: index === 0 && isSet ? "#4CAF50" : "#fff",
                            }}
                          >
                            {item.tagno}
                            {index === 0 && isSet && (
                              <Typography
                                component="span"
                                sx={{
                                  ml: 1,
                                  fontSize: 10,
                                  bgcolor: "#4CAF50",
                                  color: "#fff",
                                  px: 0.5,
                                  py: 0.2,
                                  borderRadius: 0.5,
                                }}
                              >
                                P
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </TableCell>

                    {/* Item */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.itemtype_name}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Design */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.design_name}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Gross */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {parseFloat(item.gross).toFixed(3)}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Image */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                            cursor: "pointer",
                          }}
                          onClick={() => openDialog(group)}
                        >
                          {item.imageUrl ? (
                            <SecureImage
                              item={item}
                              width={80}
                              height={80}
                              borderRadius={2}
                            />
                          ) : (
                            <Typography sx={{ color: "#aaa" }}>--</Typography>
                          )}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Total Gross */}
                    {orderFlag === "s" && (
                      <TableCell sx={{ verticalAlign: "top", p: 0 }}>
                        <Box
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#4CAF50",
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            {calculateTotalGross(group)} g
                          </Typography>
                        </Box>
                      </TableCell>
                    )}

                    {/* Note */}
                    <TableCell sx={{ verticalAlign: "top", p: 0 }}>
                      <Box
                        sx={{
                          minHeight: 70,
                          display: "flex",
                          alignItems: "center",
                          px: 2,
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder="Enter a note"
                          value={groupNotes[group.parent.tagno] || ""}
                          onChange={(e) =>
                            setGroupNotes((prev) => ({
                              ...prev,
                              [group.parent.tagno]: e.target.value,
                            }))
                          }
                          sx={{
                            width: isMobile ? "100%" : "130px",
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "transparent !important",
                              color: "#fff",
                              height: "36px",
                              borderRadius: "6px",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#666",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#aaa",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#fff",
                            },
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    {status !== "Rejected" && status !== "Dispatched" && (
                      <TableCell sx={{ verticalAlign: "top", p: 0 }}>
                        {rowStatus !== "Reject(DM)" &&
                          rowStatus !== "Dispatched(SP)" && (
                            <Box
                              sx={{
                                minHeight: 70,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                px: 2,
                              }}
                            >
                              {rowStatus !== "Unavailable(WH)" &&
                                rowStatus !== "Accept(WH)" &&
                                rowStatus !== "Order(DM)" &&
                                rowStatus !== "Confirm(DM)" && (
                                  <Button
                                    variant="contained"
                                    sx={{
                                      borderRadius: 1.5,
                                      height: "26px",
                                      bgcolor: "#257b7aff",
                                      gap: 1,
                                      width: "120px",
                                      textTransform: "none",
                                      "&:hover": {
                                        bgcolor: "rgba(39, 88, 88, 1)",
                                      },
                                    }}
                                    onClick={() =>
                                      handleUpdateStatus(group, "Confirm(DM)")
                                    }
                                  >
                                    Ready{" "}
                                    <DoneAllRoundedIcon sx={{ fontSize: 18 }} />
                                  </Button>
                                )}

                              {rowStatus !== "Accept(WH)" &&
                                rowStatus !== "Order(DM)" &&
                                rowStatus !== "Confirm(DM)" && (
                                  <Button
                                    variant="contained"
                                    sx={{
                                      borderRadius: 1.5,
                                      height: "26px",
                                      bgcolor: "#3A506B",
                                      width: "120px",
                                      textTransform: "none",
                                      gap: 1,
                                      "&:hover": { bgcolor: "#26374cff" },
                                    }}
                                    onClick={() =>
                                      handlePlaceOrder(group, "Accept(WH)")
                                    }
                                  >
                                    Place{" "}
                                    <HardwareRoundedIcon
                                      sx={{ fontSize: 18 }}
                                    />
                                  </Button>
                                )}

                              <Button
                                variant="contained"
                                sx={{
                                  borderRadius: 1.5,
                                  height: "26px",
                                  bgcolor: "#90412cff",
                                  width: "120px",
                                  textTransform: "none",
                                  gap: 1,
                                  "&:hover": {
                                    bgcolor: "rgba(127, 51, 51, 1)",
                                  },
                                }}
                                onClick={() => {
                                  setRejectionPopupOpen(true);
                                  setItemsToReject(group);
                                }}
                              >
                                Reject{" "}
                                <DeleteOutlineRoundedIcon
                                  sx={{ fontSize: 18 }}
                                />
                              </Button>
                            </Box>
                          )}
                      </TableCell>
                    )}

                    {/* Status */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            fontWeight: "bold",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.orderStatus || "New"}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Rejection Reason */}
                    {status === "Rejected" && (
                      <TableCell
                        sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                      >
                        {allItems.map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              minHeight: 70,
                              display: "flex",
                              alignItems: "center",
                              px: 2,
                              borderBottom:
                                index < allItems.length - 1
                                  ? "1px solid rgba(255,255,255,0.06)"
                                  : "none",
                            }}
                          >
                            {item?.rejectedReason || "-"}
                          </Box>
                        ))}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Box>

      {/* IMAGE PREVIEW DIALOG */}
      <Dialog
        open={dialogIndex >= 0}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogContent
          sx={{
            position: "relative",
            textAlign: "center",
            height: "70vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            overflow: "hidden",
            p: 2,
          }}
        >
          {/* Close Icon */}
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "black",
              zIndex: 20,
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          {/* Image Count */}
          {currentItems.length > 0 && (
            <Typography
              sx={{
                position: "absolute",
                bottom: 12,
                right: 12,
                color: "#000",
                fontWeight: 600,
                zIndex: 20,
              }}
            >
              {dialogIndex + 1} / {currentItems.length}
            </Typography>
          )}

          {/* Next/Prev Icons */}
          {currentItems.length > 1 && (
            <>
              <IconButton
                onClick={() => navigateDialog("prev")}
                sx={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.5)",
                }}
              >
                <ChevronLeftIcon sx={{ color: "#fff" }} />
              </IconButton>
              <IconButton
                onClick={() => navigateDialog("next")}
                sx={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.5)",
                }}
              >
                <ChevronRightIcon sx={{ color: "#fff" }} />
              </IconButton>
            </>
          )}

          {/* Image */}
          {currentItem && (
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 2,
                alignItems: "center",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minWidth: 0,
                }}
              >
                <SecureImage
                  item={currentItem}
                  width={"100%"}
                  height={"100%"}
                  borderRadius={2}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  textAlign: "left",
                  overflowY: "auto",
                  height: "100%",
                  pr: 1,
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>
                  {currentItem.itemtype_name} ({currentItem.design_name})
                </Typography>
                <Typography>Tag No: {currentItem.tagno}</Typography>
                <Typography>
                  Gross: {parseFloat(currentItem.gross).toFixed(3)}
                </Typography>
                <Typography>
                  Net Wt: {parseFloat(currentItem.netwt).toFixed(3)}
                </Typography>
                <Typography>Pcs: {currentItem.pcs}</Typography>
                <Typography>Category: {currentItem.category}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Reason Popup */}
      {rejectionPopupOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(6px)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "90%",
              maxWidth: 450,
              p: 3,
              borderRadius: 3,
              bgcolor: "#0e1c2bff",
              position: "relative",
              boxShadow: "0 4px 25px rgba(255, 255, 255, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 100,
                bgcolor: "#fff",
                "&:hover": { backgroundColor: "#f5f5f5" },
                position: "absolute",
                top: 8,
                right: 8,
                cursor: "pointer",
                p: 0.5,
              }}
              onClick={() => {
                setRejectionReason("");
                setRejectionPopupOpen(false);
              }}
            >
              <CloseRoundedIcon
                sx={{ fontSize: 22, color: "#F44336", stroke: "#F44336" }}
              />
            </Box>

            <Typography
              sx={{ fontSize: 22, fontWeight: 600, color: "#fff", mb: 2 }}
            >
              Please state a reason for rejection
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              InputProps={{
                style: { color: "#fff" },
              }}
              InputLabelProps={{
                style: { color: "#fff" },
              }}
              sx={{
                mb: 2,
                "& textarea": {
                  overflow: "hidden !important",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#fff",
                  },
                  "&:hover fieldset": {
                    borderColor: "#fff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#fff",
                  },
                },
              }}
            />

            <Box
              sx={{
                mt: 1,
                py: 1,
                textAlign: "center",
                bgcolor: rejectionReason.trim() === "" ? "#9e9e9e" : "#F44336",
                color: "#fff",
                borderRadius: 2,
                cursor:
                  rejectionReason.trim() === "" ? "not-allowed" : "pointer",
                fontWeight: 600,
                opacity: rejectionReason.trim() === "" ? 0.6 : 1,
                transition: "0.2s",
              }}
              onClick={() => {
                if (rejectionReason.trim() === "") return;
                handleUpdateStatus(itemsToReject, "Reject(DM)");
              }}
            >
              Confirm
            </Box>
          </Box>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderDetailsTable;
